// src/pages/Chat/MessagePanel.jsx
//
// FIX: replaced all `useChatStore(s => s.X[id] || [])` selectors with
// `useChatStore(s => s.X[id] ?? EMPTY_ARRAY)` using the stable sentinel
// from chatStore. The `|| []` form creates a new array reference on every
// render when the key is missing, causing an infinite update loop.

import React, { useEffect, useRef, useCallback, useState, useLayoutEffect } from "react";
import { FiUsers, FiMoreVertical, FiLoader } from "react-icons/fi";
import useChatStore, { EMPTY_ARRAY } from "../../store/chatStore";
import { resolvePhotoUrl } from "../../utils/photoHelper";
import useAuthStore from "../../store/authStore";
import { ChatCrypto } from "../../utils/crypto";
import {
  getMessages,
  editMessage,
  deleteMessage,
  addReaction,
  removeReaction,
  clearConversation,
  muteConversation,
  markMessagesRead,
} from "../../api/chatService";
import MessageBubble from "./MessageBubble";
import MessageInput from "./MessageInput";
import TypingIndicator from "./TypingIndicator";
import { useMessageDecryption } from "../../hooks/useMessageDecryption";
import UserProfileModal from "./UserProfileModal";
import GroupSettingsModal from "./GroupSettingsModal";
import { PresenceText } from "./PresenceDot";
import { FiUser, FiSearch, FiBellOff, FiTrash2, FiX, FiMaximize2, FiMinimize2, FiEyeOff } from "react-icons/fi";

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name = "") {
  return (
    name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?"
  );
}

function getDateSeparatorText(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  const now = new Date();

  const dDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const diffTime = nowDate - dDate;
  const diffDays = Math.round(diffTime / 86400000);

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7 && diffDays > 0) {
    return d.toLocaleDateString([], { weekday: "long" });
  }
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function resolveDisplayName(conversation, currentUserId) {
  if (!conversation) return "";
  const { conv_type, name, members = [] } = conversation;
  if (conv_type !== "DIRECT") return name || conv_type;

  const other = members.find((m) => {
    const id =
      m.employee_id ??
      m.user?.employee_id ??
      m.user_info?.employee_id;
    return id && String(id) !== String(currentUserId);
  });
  if (!other) return name || "Direct Message";
  return (
    other.full_name ??
    other.user?.full_name ??
    other.user_info?.full_name ??
    other.employee_id ??
    "Direct Message"
  );
}

function resolveOtherEmployeeId(conversation, currentUserId) {
  if (!conversation || conversation.conv_type !== "DIRECT") return null;
  const other = (conversation.members ?? []).find((m) => {
    const id = m.employee_id ?? m.user?.employee_id ?? m.user_info?.employee_id;
    return id && String(id) !== String(currentUserId);
  });
  return other?.employee_id ?? other?.user?.employee_id ?? other?.user_info?.employee_id ?? null;
}

function resolveAvatarUrl(conversation, currentUserId, myPhoto) {
  if (!conversation) return null;
  if (conversation.avatar_url) return resolvePhotoUrl(conversation.avatar_url);

  if (conversation.conv_type === "SELF") {
    return resolvePhotoUrl(myPhoto);
  }

  if (conversation.conv_type === "DIRECT") {
    const other = (conversation.members ?? []).find((m) => {
      const id = m.employee_id ?? m.user?.employee_id ?? m.user_info?.employee_id;
      return id && String(id) !== String(currentUserId);
    });
    const rawPhoto =
      other?.profile_photo ||
      other?.photo ||
      other?.user?.profile_photo ||
      other?.user?.photo ||
      other?.user_info?.profile_photo ||
      other?.user_info?.photo ||
      other?.avatar ||
      other?.user?.avatar ||
      other?.user_info?.avatar ||
      null;
    return resolvePhotoUrl(rawPhoto);
  }
  return null;
}

function ConvHeaderAvatar({ photo, name, initials }) {
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    setImgErr(false);
  }, [photo]);

  if (photo && !imgErr) {
    return (
      <img
        src={photo}
        alt={name}
        className="w-full h-full object-cover"
        onError={() => setImgErr(true)}
      />
    );
  }
  return <>{initials}</>;
}

// ── Delete confirm ────────────────────────────────────────────────────────────

function DeleteDialog({ onConfirm, onCancel, isOwn, isConvAdmin }) {
  const canDeleteForEveryone = isOwn || isConvAdmin;

  return (
    <>
      <div
        className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="fixed z-[81] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px]">
        <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-5 pb-4">
            <h3 className="text-sm font-semibold text-slate-100 mb-1">
              Delete message?
            </h3>
            <p className="text-[11px] leading-relaxed text-slate-400">
              {canDeleteForEveryone
                ? "Would you like to delete this message for yourself, or for everyone in the chat?"
                : "This message will be removed for you. Others will still see it."}
            </p>
          </div>

          <div className="p-2 pt-0 flex flex-col gap-1">
            {canDeleteForEveryone && (
              <button
                onClick={() => onConfirm("everyone")}
                className="w-full px-4 py-2.5 rounded-xl text-xs font-medium text-red-400 hover:bg-red-400/10 transition-colors flex items-center gap-3"
              >
                <div className="w-6 h-6 rounded-lg bg-red-400/10 flex items-center justify-center">
                  <FiTrash2 size={12} />
                </div>
                Delete for Everyone
              </button>
            )}

            <button
              onClick={() => onConfirm("me")}
              className="w-full px-4 py-2.5 rounded-xl text-xs font-medium text-slate-300 hover:bg-white/5 transition-colors flex items-center gap-3"
            >
              <div className="w-6 h-6 rounded-lg bg-white/5 flex items-center justify-center">
                <FiUser size={12} />
              </div>
              Delete for Me
            </button>

            <button
              onClick={onCancel}
              className="w-full px-4 py-2.5 mt-1 rounded-xl text-xs font-medium text-slate-500 hover:text-slate-400 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Conversation header ───────────────────────────────────────────────────────

function ConvHeader({
  conversation,
  currentUserId,
  onViewProfile,
  onViewGroupSettings,
  isSearching,
  setIsSearching,
  searchQuery,
  setSearchQuery,
  onMuteToggle,
  onClearHistory
}) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  const isMaximized = useChatStore((s) => s.isMaximized);
  const toggleIsMaximized = useChatStore((s) => s.toggleIsMaximized);
  const currentUserIdFromStore = useChatStore((s) => s.currentUserId);
  const selectConversation = useChatStore((s) => s.selectConversation);

  const resolvedUserId = currentUserId || currentUserIdFromStore;
  const myPhoto = useAuthStore((s) => s.profilePhoto);
  const avatarUrl = resolveAvatarUrl(conversation, resolvedUserId, myPhoto);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!conversation) return null;
  const isDirect = conversation.conv_type === "DIRECT";
  const isSelf = conversation.conv_type === "SELF";
  const isDirectOrSelf = isDirect || isSelf;
  const displayName = resolveDisplayName(conversation, resolvedUserId);
  const otherEmployeeId = resolveOtherEmployeeId(conversation, resolvedUserId);
  const activeCount = (conversation.members ?? []).filter(
    (m) => m.is_active !== false
  ).length;

  const handleAction = (action) => {
    setShowMenu(false);
    if (action === "profile") {
      if (isDirectOrSelf) onViewProfile();
      else onViewGroupSettings();
    }
    if (action === "search") setIsSearching(true);
    if (action === "mute") onMuteToggle();
    if (action === "clear") onClearHistory();
  };

  if (isSearching) {
    return (
      <div className="flex items-center gap-3 px-5 py-3 border-b border-white/8 bg-slate-900/50 backdrop-blur-sm shrink-0 h-[61px] relative z-[100]">
        <FiSearch className="text-slate-500" size={16} />
        <input
          autoFocus
          type="text"
          placeholder="Search in conversation..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 bg-transparent border-none outline-none text-slate-200 text-sm"
        />
        <button
          onClick={() => { setIsSearching(false); setSearchQuery(""); }}
          className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-slate-200 transition-colors"
        >
          <FiX size={18} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between px-5 py-3 border-b border-white/8 bg-slate-900/50 backdrop-blur-sm shrink-0 relative z-[100]">
      <div className="flex items-center gap-3">
        {/* Back button (Mobile only) */}
        <button
          onClick={() => selectConversation(null)}
          className="md:hidden p-1.5 -ml-1 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-colors cursor-pointer flex items-center justify-center"
          title="Back to conversations"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
        </button>
        <button
          onClick={isDirectOrSelf ? onViewProfile : onViewGroupSettings}
          className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center text-white text-xs font-bold select-none shadow hover:scale-105 transition-transform overflow-hidden animate-in fade-in zoom-in duration-200"
        >
          <ConvHeaderAvatar
            photo={avatarUrl}
            name={displayName}
            initials={getInitials(displayName)}
          />
        </button>
        <div>
          <div className="flex flex-col">
            <span
              onClick={isDirectOrSelf ? onViewProfile : onViewGroupSettings}
              className="text-sm font-semibold text-slate-100 cursor-pointer hover:text-blue-400 transition-colors"
            >
              {displayName}
            </span>
            {isDirect && otherEmployeeId && (
              <PresenceText employeeId={otherEmployeeId} />
            )}
          </div>
          {!isDirectOrSelf && (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <FiUsers size={10} /> {activeCount} members
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1">
        {/* Issue 13: Maximize/Minimize Toggle */}
        <button
          onClick={toggleIsMaximized}
          className="p-2 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-all"
          title={isMaximized ? "Restore" : "Maximize"}
        >
          {isMaximized ? <FiMinimize2 size={16} /> : <FiMaximize2 size={16} />}
        </button>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className={`p-2 rounded-lg transition-all ${showMenu ? "text-blue-400 bg-white/10" : "text-slate-500 hover:text-slate-300 hover:bg-white/5"}`}
          >
            <FiMoreVertical size={16} />
          </button>

          {showMenu && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-slate-800 border border-white/10 rounded-xl shadow-2xl z-[200] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
              <div className="py-1">
                <button onClick={() => handleAction("profile")} className="w-full px-4 py-2 text-left text-xs text-slate-300 hover:bg-white/5 flex items-center gap-2">
                  <FiUser size={14} className="text-slate-500" /> {isDirectOrSelf ? "View Profile" : "Group Info"}
                </button>
                <button onClick={() => handleAction("search")} className="w-full px-4 py-2 text-left text-xs text-slate-300 hover:bg-white/5 flex items-center gap-2">
                  <FiSearch size={14} className="text-slate-500" /> Search in Chat
                </button>
                <button onClick={() => handleAction("mute")} className="w-full px-4 py-2 text-left text-xs text-slate-300 hover:bg-white/5 flex items-center gap-2">
                  <FiBellOff size={14} className={conversation.is_muted ? "text-blue-400" : "text-slate-500"} />
                  {conversation.is_muted ? "Unmute Notifications" : "Mute Notifications"}
                </button>
                <div className="border-t border-white/5 my-1" />
                <button onClick={() => handleAction("clear")} className="w-full px-4 py-2 text-left text-xs text-red-400 hover:bg-red-400/10 flex items-center gap-2">
                  <FiTrash2 size={14} /> Clear Chat History
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Message grouping ──────────────────────────────────────────────────────────

function buildGroups(msgList, currentUserId) {
  return msgList.map((item, i) => {
    const prev = msgList[i - 1];
    const getSid = (m) =>
      m?.sender?.employee_id ?? m?.sender_id ?? null;
    const sameRun =
      prev && !prev.is_deleted && getSid(prev) && getSid(prev) === getSid(item);
    const isOwn = getSid(item) === currentUserId;
    return { item, isOwn, showAvatar: !sameRun };
  });
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function MessagePanel({ send, connected, currentUserId, width }) {
  const selectedConvId = useChatStore((s) => s.selectedConvId);
  const conversations = useChatStore((s) => s.conversations);
  const isNarrow = typeof width === "number" && width < 360;

  const currentUserIdFromStore = useChatStore((s) => s.currentUserId);
  const resolvedUserId = currentUserId || currentUserIdFromStore;

  const messages = useChatStore(
    (s) => s.messages[selectedConvId] ?? EMPTY_ARRAY
  );
  const hasMore = useChatStore((s) => s.hasMore[selectedConvId] ?? false);
  const cursor = useChatStore((s) => s.cursors[selectedConvId] ?? null);

  // Background Decryption
  useMessageDecryption(selectedConvId);

  const setMessages = useChatStore((s) => s.setMessages);
  const prependMessages = useChatStore((s) => s.prependMessages);
  const updateMessage = useChatStore((s) => s.updateMessage);
  const deleteMsg = useChatStore((s) => s.deleteMessage);
  const clearUnread = useChatStore((s) => s.clearUnread);
  const updateConversation = useChatStore((s) => s.updateConversation);

  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [error, setError] = useState(null);

  const [replyTo, setReplyTo] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [profileTarget, setProfileTarget] = useState(null);
  const [groupSettingsTarget, setGroupSettingsTarget] = useState(null);

  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const prevConvRef = useRef(null);
  const atBottomRef = useRef(true);
  const prevCountRef = useRef(0);
  const prevScrollHeight = useRef(null);

  const conversation = conversations.find((c) => c.id === selectedConvId) ?? null;

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 100;

    // Auto-load older messages when scrolled near the top
    if (el.scrollTop < 100 && hasMore && !loadingMore && !loadingInitial) {
      loadMore();
    }
  };

  useEffect(() => {
    window.__chat_send = send;
    return () => { delete window.__chat_send; };
  }, [send]);

  // ── Load messages when conversation changes ───────────────────────────────
  useEffect(() => {
    if (!selectedConvId) return;

    setReplyTo(null);
    setEditTarget(null);
    setDeleteTarget(null);
    setProfileTarget(null);
    setGroupSettingsTarget(null);
    setError(null);
    setLoadMoreFailed(false);

    const load = async () => {
      const storeMsgs = useChatStore.getState().messages[selectedConvId];
      if (!storeMsgs || storeMsgs.length === 0) {
        setLoadingInitial(true);
      }
      try {
        const { data } = await getMessages(selectedConvId);
        const msgs = data.messages ?? [];
        setMessages(selectedConvId, msgs, data.next_cursor, data.has_more);
        clearUnread(selectedConvId);
      } catch (err) {
        console.error("[MessagePanel] load failed:", err);
        setError("Failed to load messages.");
      } finally {
        setLoadingInitial(false);
      }
    };
    load();
  }, [selectedConvId, clearUnread, setMessages]);

  // Scroll to bottom after initial load
  useEffect(() => {
    if (!loadingInitial && messages.length > 0) {
      requestAnimationFrame(() =>
        bottomRef.current?.scrollIntoView({ behavior: "instant" })
      );
    }
  }, [loadingInitial]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll on new messages + Send seen receipts
  useEffect(() => {
    const newCount = messages.length;
    if (newCount > prevCountRef.current && atBottomRef.current) {
      requestAnimationFrame(() =>
        bottomRef.current?.scrollIntoView({ behavior: "smooth" })
      );
    }

    // Mark messages as seen if we are active
    if (selectedConvId && messages.length > 0) {
      const allIds = messages.map(m => m.id).filter(Boolean);
      const unreadIds = messages
        .filter(m => !m.is_read_by_me && (m.sender_id || m.sender?.employee_id) !== resolvedUserId)
        .map(m => m.id)
        .filter(Boolean);

      const idsToMark = unreadIds.length > 0 ? unreadIds : allIds;

      if (idsToMark.length > 0) {
        if (unreadIds.length > 0 && send) {
          send({
            type: "message_seen",
            conv_id: selectedConvId,
            message_ids: unreadIds
          });
        }
        markMessagesRead(selectedConvId, idsToMark).catch(() => { });
        clearUnread(selectedConvId);
      }
    }

    prevCountRef.current = newCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, selectedConvId, resolvedUserId, send, markMessagesRead, clearUnread]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || prevCountRef.current === messages.length) return;

    if (loadingMore && prevScrollHeight.current !== null) {
      // We just prepended messages. Restore scroll position so it doesn't jump.
      const diff = el.scrollHeight - prevScrollHeight.current;
      el.scrollTop += diff;
      prevScrollHeight.current = null;

      // If the newly loaded messages took up almost no space (e.g. they were all invisible POLL_VOTEs),
      // we are still at the top, but the user doesn't know. We must automatically trigger another load.
      // We use a 500ms delay to prevent rapid-fire requests from triggering IIS rate-limiting (403 errors).
      if (diff < 50 && el.scrollTop < 100) {
        setTimeout(() => el.dispatchEvent(new Event("scroll")), 500);
      }
    } else if (atBottomRef.current || messages.length <= 50) {
      // First load or was at bottom — scroll to bottom
      el.scrollTop = el.scrollHeight;
    }
    prevCountRef.current = messages.length;
  }, [messages, loadingMore]);

  // ── Load older messages ───────────────────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || !selectedConvId || !cursor) return;
    setLoadingMore(true);
    const el = scrollRef.current;
    prevScrollHeight.current = el?.scrollHeight ?? 0;
    try {
      const { data } = await getMessages(selectedConvId, cursor);
      const msgs = data.messages ?? [];
      prependMessages(selectedConvId, msgs, data.next_cursor, data.has_more);
      setLoadMoreFailed(false);
    } catch (err) {
      console.error("[MessagePanel] loadMore failed:", err);
      setLoadMoreFailed(true);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, selectedConvId, cursor, prependMessages]);

  // ── Filtered messages ─────────────────────────────────────────────────────
  const filteredMessages = React.useMemo(() => {
    return messages.filter(m => {
      if (m.deleted_for_me) return false;
      if (m.msg_type === "POLL_VOTE") return false;
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      const content = (m.content || "").toLowerCase();
      const dec = (m.decryptedContent || "").toLowerCase();
      return content.includes(q) || dec.includes(q);
    });
  }, [messages, searchQuery]);

  // Auto-fetch older pages if current page doesn't have enough visible messages to fill the screen
  // (e.g. if the API returned 50 messages but 48 of them were background poll votes)
  useEffect(() => {
    if (!loadingInitial && !loadingMore && hasMore && !loadMoreFailed && messages.length > 0) {
      const el = scrollRef.current;
      // Fetch more if we have less than 15 visible messages, or if there's no scrollbar
      if (filteredMessages.length < 15 || (el && el.scrollHeight <= el.clientHeight)) {
        console.log("[MessagePanel] Not enough visible messages to fill view, auto-fetching older messages...");
        const timer = setTimeout(() => loadMore(), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [loadingInitial, loadingMore, hasMore, messages.length, filteredMessages.length, loadMoreFailed, loadMore]);



  // ── Reactions ─────────────────────────────────────────────────────────────
  const handleReact = useCallback(
    async (messageId, emoji) => {
      try {
        const { data } = await addReaction(messageId, emoji);
        updateMessage(selectedConvId, messageId, {
          reactions: data.reaction_counts ?? data.reactions,
        });
      } catch (err) {
        console.error("addReaction failed:", err);
      }
    },
    [selectedConvId, updateMessage]
  );

  const handleRemoveReact = useCallback(
    async (messageId, emoji) => {
      try {
        const { data } = await removeReaction(messageId, emoji);
        updateMessage(selectedConvId, messageId, {
          reactions: data.reaction_counts ?? data.reactions,
        });
      } catch (err) {
        console.error("removeReaction failed:", err);
      }
    },
    [selectedConvId, updateMessage]
  );

  // ── Edit ──────────────────────────────────────────────────────────────────
  const handleEditSubmit = useCallback(
    async (messageId, newContent) => {
      try {
        const aesKey = useChatStore.getState().conversationKeys[selectedConvId];
        let payload = { content: newContent };
        if (aesKey) {
          const { ciphertext, iv } = await ChatCrypto.encrypt(newContent, aesKey);
          payload = { ciphertext, iv, content: newContent };
        }
        await editMessage(messageId, payload);
        updateMessage(selectedConvId, messageId, {
          content: newContent,
          ciphertext: payload.ciphertext || "",
          iv: payload.iv || "",
          decryptedContent: newContent,
          is_edited: true,
          edited_at: new Date().toISOString(),
        });
      } catch (err) {
        console.error("editMessage failed:", err);
      }
      setEditTarget(null);
    },
    [selectedConvId, updateMessage]
  );

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = useCallback(async (type = "everyone") => {
    if (!deleteTarget) return;
    try {
      await deleteMessage(deleteTarget.id, type);
      if (type === "everyone") {
        updateMessage(selectedConvId, deleteTarget.id, {
          is_deleted: true,
          deleted_at: new Date().toISOString(),
          ciphertext: "",
          content: "",
        });
      } else {
        // Just remove from local store
        deleteMsg(selectedConvId, deleteTarget.id);
      }
    } catch (err) {
      console.error("deleteMessage failed:", err);
    }
    setDeleteTarget(null);
  }, [deleteTarget, selectedConvId, deleteMsg, updateMessage]);

  // ── No conversation selected ──────────────────────────────────────────────
  if (!selectedConvId) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-slate-500">
        <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-3xl select-none">
          💬
        </div>
        <p className="text-sm">Select a conversation to start messaging</p>
      </div>
    );
  }



  const handleMuteToggle = async () => {
    if (!conversation) return;
    try {
      const isMuted = !conversation.is_muted;
      await muteConversation(selectedConvId, isMuted);
      updateConversation(selectedConvId, { is_muted: isMuted });
    } catch (err) {
      console.error("Mute failed:", err);
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm("Are you sure you want to clear chat history? This cannot be undone.")) return;
    try {
      await clearConversation(selectedConvId);
      // Clear local messages
      setMessages(selectedConvId, [], null, false);
    } catch (err) {
      console.error("Clear history failed:", err);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <ConvHeader
        conversation={conversation}
        currentUserId={resolvedUserId}
        isSearching={isSearching}
        setIsSearching={setIsSearching}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onMuteToggle={handleMuteToggle}
        onClearHistory={handleClearHistory}
        onViewGroupSettings={() => setGroupSettingsTarget(conversation)}
        onViewProfile={() => {
          const isDirect = conversation.conv_type === "DIRECT";
          if (isDirect) {
            const other = (conversation.members ?? []).find(m => (m.employee_id || m.user?.employee_id) !== resolvedUserId);
            if (other) {
              setProfileTarget({
                employee_id: other.employee_id || other.user?.employee_id,
                full_name: other.full_name || other.user?.full_name || resolveDisplayName(conversation, resolvedUserId),
                email: other.email || other.user?.email,
                role: other.role || other.designation,
                department: other.department || other.dept,
                photo: other.photo || other.profile_photo || other.user?.photo || other.user?.profile_photo || other.user_info?.photo || other.user_info?.profile_photo || other.avatar || other.user?.avatar || other.user_info?.avatar
              });
            }
          }
        }}
      />

      {/* ── Message list or Narrow placeholder ── */}
      {isNarrow ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center select-none text-slate-500 gap-3 bg-slate-900/10">
          <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-slate-400">
            <FiEyeOff size={22} className="text-slate-450 animate-pulse" />
          </div>
          <div className="text-sm font-semibold text-slate-300">Panel Too Narrow</div>
          <div className="text-xs text-slate-500 max-w-[220px] leading-relaxed">
            Drag the divider border to the left to expand the chat room and view messages.
          </div>
        </div>
      ) : (
        <>
          {/* ── Message list ── */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto py-3 pr-2"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(255,255,255,0.25) transparent",
            }}
          >
            {loadingMore && (
              <div className="flex justify-center py-3">
                <FiLoader size={14} className="text-slate-500 animate-spin" />
              </div>
            )}

            {loadingInitial && (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <div className="w-6 h-6 border-2 border-blue-500/40 border-t-blue-500 rounded-full animate-spin" />
                <span className="text-xs text-slate-500">Loading messages…</span>
              </div>
            )}

            {error && !loadingInitial && (
              <div className="text-center py-8">
                <p className="text-xs text-red-400">{error}</p>
                <button
                  onClick={() => {
                    prevConvRef.current = null;
                    setError(null);
                  }}
                  className="text-xs text-blue-400 hover:text-blue-300 mt-2 transition-colors"
                >
                  Retry
                </button>
              </div>
            )}

            {!loadingInitial && !error && filteredMessages.length === 0 && (
              <div className="text-center py-16 text-slate-600 text-sm select-none">
                {searchQuery ? `No messages matching "${searchQuery}"` : "No messages yet. Say hello! 👋"}
              </div>
            )}

            <div className="space-y-0.5">
              {buildGroups(filteredMessages, resolvedUserId).map(({ item, isOwn, showAvatar }, index) => {
                let showDateSeparator = false;
                let dateText = "";
                if (index === 0) {
                  showDateSeparator = true;
                  dateText = getDateSeparatorText(item.created_at);
                } else {
                  const prevItem = filteredMessages[index - 1];
                  const currentDate = new Date(item.created_at).toDateString();
                  const prevDate = new Date(prevItem.created_at).toDateString();
                  if (currentDate !== prevDate) {
                    showDateSeparator = true;
                    dateText = getDateSeparatorText(item.created_at);
                  }
                }

                return (
                  <React.Fragment key={item.id ?? item.client_msg_id}>
                    {showDateSeparator && (
                      <div className="flex justify-center my-4 select-none">
                        <span className="text-[10px] font-semibold text-slate-400 bg-white/5 border border-white/8 rounded-full px-3 py-1 shadow-sm">
                          {dateText}
                        </span>
                      </div>
                    )}
                    <MessageBubble
                      message={item}
                      isOwn={isOwn}
                      showAvatar={showAvatar}
                      currentUserId={resolvedUserId}
                      isConvAdmin={conversation?.my_role === "ADMIN"}
                      onReply={(msg) => { setEditTarget(null); setReplyTo(msg); }}
                      onEdit={(msg) => { setReplyTo(null); setEditTarget(msg); }}
                      onDelete={(msg) => setDeleteTarget(msg)}
                      onReact={handleReact}
                      onRemoveReact={handleRemoveReact}
                      onViewProfile={(sender) => {
                        setProfileTarget({
                          employee_id: sender.employee_id,
                          full_name: sender.full_name || (sender.first_name ? `${sender.first_name} ${sender.last_name || ""}`.trim() : null),
                          email: sender.email,
                          role: sender.role || sender.designation,
                          department: sender.department || sender.dept,
                          photo: sender.photo || sender.profile_photo
                        });
                      }}
                    />
                  </React.Fragment>
                );
              })}
            </div>

            <div ref={bottomRef} />
          </div>

          <TypingIndicator />

          <MessageInput
            send={send}
            connected={connected}
            replyTo={replyTo}
            editTarget={editTarget}
            onCancelReply={() => setReplyTo(null)}
            onCancelEdit={() => setEditTarget(null)}
            onEditSubmit={handleEditSubmit}
          />
        </>
      )}

      {deleteTarget && (
        <DeleteDialog
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          isOwn={(deleteTarget.sender?.employee_id ?? deleteTarget.sender_id) === resolvedUserId}
          isConvAdmin={conversation?.my_role === "ADMIN"}
        />
      )}

      {groupSettingsTarget && (
        <GroupSettingsModal
          conversation={groupSettingsTarget}
          currentUserId={resolvedUserId}
          onClose={() => setGroupSettingsTarget(null)}
        />
      )}

      {profileTarget && (
        <UserProfileModal
          user={profileTarget}
          onClose={() => setProfileTarget(null)}
        />
      )}
    </div>
  );
}